export default class APIClient {
  private baseURL = `${process.env.SARKANNIEMI_API_URI}/v1`;

  public async requestTicketHOTP(
    ticketId: string,
  ): Promise<{ status: number | null }> {
    const url = `${this.baseURL}/hotp`;

    try {
      const body: Paths.GenerateHOTP.RequestBody = {
        resource: "ticket",
        id: ticketId,
      };

      const { status } = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      return { status };
    } catch {
      return { status: null };
    }
  }

  public async fetchSeasonCardHolder(
    ticketId: string,
    hotp: string,
  ): Promise<{
    status: number | null;
    holder?: Paths.FetchSeasonCardHolder.Responses.$200;
  }> {
    const url = `${this.baseURL}/ecommerce/season-pass/${ticketId}/holder`;

    try {
      const response = await fetch(url, {
        headers: {
          Authorization: hotp,
        },
      });

      switch (response.status) {
        case 200: {
          return {
            status: response.status,
            holder:
              (await response.json()) as Paths.FetchSeasonCardHolder.Responses.$200,
          };
        }
        default: {
          return {
            status: response.status,
          };
        }
      }
    } catch {
      return {
        status: null,
      };
    }
  }

  public async fetchPurchase(purchaseId: string): Promise<{
    status: number | null;
    purchase: Paths.FetchPurchase.Responses.$200 | null;
  }> {
    const url = `${this.baseURL}/purchase/${purchaseId}`;

    try {
      const response = await fetch(url);

      if (response.status === 200) {
        return {
          status: response.status,
          purchase:
            (await response.json()) as Paths.FetchPurchase.Responses.$200,
        };
      } else {
        return {
          status: response.status,
          purchase: null,
        };
      }
    } catch {
      return {
        status: null,
        purchase: null,
      };
    }
  }

  public async updatePurchase(
    purchaseId: string,
    body: Paths.UpdatePurchase.RequestBody,
  ): Promise<{
    status: number | null;
    locationHeader: string | null;
  }> {
    const url = `${this.baseURL}/purchase/${purchaseId}`;

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      return {
        status: response.status,
        locationHeader: response.headers.get("location"),
      };
    } catch {
      return { status: null, locationHeader: null };
    }
  }

  public async addTicketShare(body: Paths.AddTicketShare.RequestBody): Promise<{
    status: number | null;
    createdReferences:
      | Paths.AddTicketShare.Responses.$201["createdReferences"]
      | null;
  }> {
    const url = `${this.baseURL}/ticket-shares/`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (response.status === 201) {
        return {
          status: response.status,
          createdReferences: (
            (await response.json()) as Paths.AddTicketShare.Responses.$201
          ).createdReferences,
        };
      } else {
        return {
          status: response.status,
          createdReferences: null,
        };
      }
    } catch {
      return {
        status: null,
        createdReferences: null,
      };
    }
  }

  public async fetchSharedTickets({
    ticketIds,
    voucherIds,
  }: Paths.FetchSharedTickets.QueryParameters): Promise<{
    status: number | null;
    references: Paths.FetchSharedTickets.Responses.$200["references"] | null;
  }> {
    if (ticketIds === undefined && voucherIds === undefined) {
      return {
        status: null,
        references: [],
      };
    }

    const query = new URLSearchParams({
      ...(ticketIds !== undefined && { ticketIds: ticketIds.join(",") }),
      ...(voucherIds !== undefined && { voucherIds: voucherIds.join(",") }),
    });
    const url = `${this.baseURL}/shared-tickets/?${query.toString()}`;

    try {
      const response = await fetch(url);

      if (response.status === 200) {
        return {
          status: response.status,
          references: (
            (await response.json()) as Paths.FetchSharedTickets.Responses.$200
          ).references,
        };
      } else {
        return {
          status: response.status,
          references: null,
        };
      }
    } catch {
      return {
        status: null,
        references: null,
      };
    }
  }

  public async deleteSharedTicket(referenceId: string): Promise<{
    status: number | null;
    deletedReferenceId:
      | Paths.DeleteSharedTicket.Responses.$200["deletedReferenceId"]
      | null;
  }> {
    const url = `${this.baseURL}/shared-tickets/${referenceId}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
      });

      if (response.status === 200) {
        return {
          status: response.status,
          deletedReferenceId: (
            (await response.json()) as Paths.DeleteSharedTicket.Responses.$200
          ).deletedReferenceId,
        };
      } else {
        return {
          status: response.status,
          deletedReferenceId: null,
        };
      }
    } catch {
      return {
        status: null,
        deletedReferenceId: null,
      };
    }
  }
}
